// The cards that say something happened while the person was here or away:
// an achievement found (ACHIEVEMENTS.md §12) and a milestone a team of theirs
// reached (PLAN-2026-10-29-milestones-as-they-happen §2), ONE AT A TIME, an
// achievement's first: a milestone's card waits while one is waiting or up.
//
// WHOSE CARD IS UP IS A REF, set at the moment a card is taken and asked at
// the moment the other would show. Both cards look for a clear screen in the
// same pass when the pulse brings both, and a card taking its batch empties a
// queue that redraws this before any state set beside it lands, so a turn
// worked out in render would read nobody up and nothing waiting, and let the
// other card in. React's development mode runs a component's unmount once at
// mount, so nothing here lets go of a card but the card going.

import { useCallback, useRef } from 'react';
import { momentsWaiting } from '../data/achievements';
import { useSession } from '../data/session';
import { AchievementMoment } from './AchievementMoment';
import { MilestoneMoment } from './MilestoneMoment';

type Who = 'achievement' | 'milestone';

/** Who is up once `who` says it has shown or gone. */
const said = (was: Who | null, who: Who, on: boolean): Who | null =>
  on ? who : was === who ? null : was;

export function Moments() {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const up = useRef<Who | null>(null);
  const achievementMay = useCallback(() => up.current === null || up.current === 'achievement', []);
  const milestoneMay = useCallback(
    () => up.current === 'milestone' || (up.current === null && !momentsWaiting(userId)),
    [userId],
  );
  const achievementUp = useCallback((on: boolean) => {
    up.current = said(up.current, 'achievement', on);
  }, []);
  const milestoneUp = useCallback((on: boolean) => {
    up.current = said(up.current, 'milestone', on);
  }, []);
  return (
    <>
      <AchievementMoment mayShow={achievementMay} onUp={achievementUp} />
      <MilestoneMoment mayShow={milestoneMay} onUp={milestoneUp} />
    </>
  );
}
